import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMemo } from "react";
import { api } from "@/api/client";
import { Package, Trash2, Search, Truck, ClipboardList } from "lucide-react";
import { createPageUrl, logActivity } from "@/utils";
import { useAuth } from "@/lib/AuthContext";
import MailingCard from "@/components/MailingCard";
import BoxPackingModal from "@/components/BoxPackingModal";
import ShippingLabelsModal from "@/components/ShippingLabelsModal";
import ShipMethodDialog from "@/components/ShipMethodDialog";
import PDFViewerModal from "@/components/PDFViewerModal";
import PostOfficePVDSModal from "@/components/PostOfficePVDSModal";
import ActivityLogsModal from "@/components/ActivityLogsModal";

export default function ProcessedMailingsPage() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [mailings, setMailings] = useState([]);
  const [filteredMailings, setFilteredMailings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selectedMailings, setSelectedMailings] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [packingModalMailing, setPackingModalMailing] = useState(null);
  const [generatingPDF, setGeneratingPDF] = useState(null);
  const [shippingModalMailing, setShippingModalMailing] = useState(null);
  const [previewPDF, setPreviewPDF] = useState(null);
  const [pvdsModalMailing, setPvdsModalMailing] = useState(null);
  const [shipMethodMailing, setShipMethodMailing] = useState(null);
  const [showActivityLogs, setShowActivityLogs] = useState(false);
  const [mailingLimit, setMailingLimit] = useState(500);

  /** An order whose shipments have to go first, awaiting the go-ahead. */
  const [shipmentsInTheWay, setShipmentsInTheWay] = useState(null);
  const [deletingMailing, setDeletingMailing] = useState(null);

  useEffect(() => {
    fetchMailings();
  }, [mailingLimit]);

  const fetchMailings = async () => {
    try {
      const data = await api.entities.Mailing.list('-created_at', mailingLimit);
      setMailings(data);
      setFilteredMailings(data);
    } catch (err) {
      console.error(err);
      setError("Failed to load mailings");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Whether a mailing has shipped, decided by the database.
   *
   * This used to download the most recent 500 shipments and rebuild the set
   * here. That answer is only correct while the shop has fewer shipments than
   * the limit - at 463 and climbing, the next few months of work would have
   * pushed the oldest ones out of the window and quietly moved their mailings
   * back to the unshipped tab. The view already computes it with an EXISTS
   * over all of them, which is both right and one fewer request.
   */
  const hasShipments = (mailing) => Boolean(mailing?.is_shipped);

  useEffect(() => {
    let filtered = [...mailings];

    if (activeTab === "shipped") {
      filtered = filtered.filter(hasShipments);
    } else if (activeTab === "all") {
      filtered = filtered.filter((m) => !hasShipments(m));
    }

    if (searchTerm) {
      filtered = filtered.filter((m) => {
        const name = m.mailing_name?.toLowerCase() || "";
        const order = m.order_number?.toLowerCase() || "";
        const search = searchTerm.toLowerCase();
        return name.includes(search) || order.includes(search);
      });
    }

    if (dateFrom) {
      filtered = filtered.filter((m) => {
        const mailingDate = new Date(m.created_at);
        const fromDate = new Date(`${dateFrom}T00:00:00`);
        return mailingDate >= fromDate;
      });
    }

    if (dateTo) {
      filtered = filtered.filter((m) => {
        const mailingDate = new Date(m.created_at);
        const toDate = new Date(`${dateTo}T23:59:59`);
        return mailingDate <= toDate;
      });
    }

    setFilteredMailings(filtered);
    setSelectedMailings([]);
  }, [searchTerm, dateFrom, dateTo, mailings, activeTab]);

  const handleViewShipment = (mailing) => {
    navigate(createPageUrl('Shipments'), {
      state: { selectedOrderNumber: mailing.order_number }
    });
  };

  /**
   * Delete an order.
   *
   * An order that has shipped cannot simply go: its shipments are labels that
   * were paid for, and the money has to come back first. The server refuses and
   * says how many are attached; `withShipments` is the second pass, where it
   * refunds and removes each one and then deletes the order.
   *
   * This used to report "Failed to delete mailing" for every failure, so the
   * server's actual explanation - which said exactly what was in the way -
   * never reached anyone.
   */
  const handleDelete = async (mailingId, { withShipments = false, force = false } = {}) => {
    setDeletingMailing(mailingId);
    try {
      const mailing = mailings.find(m => m.id === mailingId);
      await api.entities.Mailing.delete(mailingId, {
        ...(withShipments ? { with_shipments: 'true' } : {}),
        ...(force ? { force: 'true' } : {}),
      });
      await logActivity(mailingId, mailing?.mailing_name, mailing?.order_number, 'DELETE', `Deleted mailing`, currentUser);
      setMailings(mailings.filter((m) => m.id !== mailingId));
      setSelectedMailings(selectedMailings.filter((id) => id !== mailingId));
      setDeleteConfirm(null);
      setShipmentsInTheWay(null);
    } catch (err) {
      console.error(err);

      // The order still has shipments. Offer to deal with them rather than
      // leaving a dead end - this is the ordinary case, not an error.
      if (err.details?.can_remove_shipments) {
        const mailing = mailings.find(m => m.id === mailingId);
        setShipmentsInTheWay({
          mailingId,
          mailing,
          count: err.details.shipment_count,
          names: err.details.shipment_names ?? [],
        });
        setDeleteConfirm(null);
        return;
      }

      setShipmentsInTheWay(null);
      setError(err.message || "Failed to delete mailing");
      setTimeout(() => setError(""), 10000);
    } finally {
      setDeletingMailing(null);
    }
  };

  const handleDeleteSelected = async () => {
    try {
      // Log activity for each mailing before deletion
      const mailingLog = selectedMailings.map(id => {
        const mailing = mailings.find(m => m.id === id);
        return { id, mailing };
      });

      for (const { id, mailing } of mailingLog) {
         if (mailing) {
           await logActivity(id, mailing.mailing_name, mailing.order_number, 'DELETE', 'Deleted mailing (Bulk)', currentUser);
         }
       }

      // Sequentially, so one failure does not leave a dozen half-finished
      // refunds in flight, and so the report can say which one stopped it.
      const deleted = [];
      for (const id of selectedMailings) {
        try {
          await api.entities.Mailing.delete(id);
          deleted.push(id);
        } catch (err) {
          const mailing = mailings.find((m) => m.id === id);
          const order = mailing?.order_number ?? 'an order';
          setMailings(mailings.filter((m) => !deleted.includes(m.id)));
          setSelectedMailings(selectedMailings.filter((x) => !deleted.includes(x)));
          setDeleteConfirm(null);

          // Shipped orders are the common case here, and the fix is per-order:
          // each one needs its own refund decision, so say so rather than
          // silently skipping them or refunding a dozen orders on one click.
          setError(
            err.details?.can_remove_shipments
              ? `Stopped at order ${order}: it has ${err.details.shipment_count} shipment(s) ` +
                `that must be refunded first. Delete that one on its own to do that. ` +
                `${deleted.length} order(s) were removed before it.`
              : `${err.message || 'Failed to delete'} (stopped at order ${order}; ` +
                `${deleted.length} removed before it).`
          );
          setTimeout(() => setError(""), 12000);
          return;
        }
      }
      setMailings(mailings.filter((m) => !selectedMailings.includes(m.id)));
      setSelectedMailings([]);
      setDeleteConfirm(null);
    } catch (err) {
      console.error(err);
      setError(err.message || "Failed to delete selected mailings");
      setTimeout(() => setError(""), 8000);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedMailings(filteredMailings.map((m) => m.id));
    } else {
      setSelectedMailings([]);
    }
  };

  const handleSelectMailing = (mailingId) => {
    if (selectedMailings.includes(mailingId)) {
      setSelectedMailings(selectedMailings.filter((id) => id !== mailingId));
    } else {
      setSelectedMailings([...selectedMailings, mailingId]);
    }
  };

  const handlePackingComplete = async (updatedMailing) => {
    setMailings(mailings.map(m => m.id === updatedMailing.id ? updatedMailing : m));
    await logActivity(
      updatedMailing.id, 
      updatedMailing.mailing_name, 
      updatedMailing.order_number, 
      'PACK_BOXES', 
      `Packed ${updatedMailing.box_packing?.total_boxes || 0} ${updatedMailing.box_packing?.container_type || 'boxes'}`,
      currentUser
    );
  };

  const handleShippingComplete = async (mailingId, mailingName, orderNumber, isDelivered = false) => {
    if (mailingId && mailingName && orderNumber) {
      await logActivity(
        mailingId,
        mailingName,
        orderNumber,
        isDelivered ? 'MARK_DELIVERED' : 'CREATE_SHIPPING_LABELS',
        isDelivered ? 'Marked as delivered (hand delivery)' : 'Created shipping labels',
        currentUser
      );
    }
    fetchMailings();
  };

  /** UI type -> the document kind the API knows about. */
  const DOCUMENT_KIND = {
    pvds: 'pvds',
    facing: 'facing-slips',
    labels: 'box-labels',
    acceptance: 'acceptance',
    postage_statement: 'postage-statement'
  };

  /** UI type -> the column recording where the generated file was stored. */
  const DOCUMENT_PATH_FIELD = {
    pvds: 'pvds_path',
    facing: 'facing_slips_path',
    labels: 'labels_path',
    acceptance: 'acceptance_pdf_path',
    postage_statement: 'postage_statement_path'
  };

  const handlePreviewPDF = async (mailing, type, forceRegenerate = false) => {
    const pdfTypes = {
      pvds: 'PVDS',
      facing: 'Facing Slips',
      labels: 'Box Labels',
      acceptance: 'Acceptance PDF',
      postage_statement: 'Postage Statement'
    };

    setGeneratingPDF(`${mailing.id}-${type}`);
    setError("");

    try {
      // Already generated: ask for a fresh signed URL rather than reusing a
      // stored link. Signed URLs expire by design, and the database clears
      // these paths automatically whenever the data behind them changes, so a
      // path that is still set is known to be current.
      if (!forceRegenerate && mailing[DOCUMENT_PATH_FIELD[type]]) {
        try {
          const { url } = await api.documents.url(mailing.id, DOCUMENT_KIND[type]);
          setPreviewPDF({
            url,
            title: `${pdfTypes[type]} — ${mailing.order_number}`,
            type
          });
          return;
        } catch (err) {
          // A stored path that cannot be signed is a reason to rebuild the
          // document, not to give up on it. This used to escape and abort the
          // whole handler, so a mailing whose paperwork already existed could
          // not be reopened or regenerated - the button simply did nothing.
          console.warn('Stored document could not be opened, regenerating:', err);
        }
      }

      const result = await api.documents.generate(mailing.id, DOCUMENT_KIND[type]);

      await logActivity({
        mailing_id: mailing.id,
        type: 'PDF_GENERATED',
        description: `Generated ${pdfTypes[type] || type}`
      });

      // Reflect the new path locally so the next open takes the cached route.
      setMailings(mailings.map(m =>
        m.id === mailing.id ? { ...m, [DOCUMENT_PATH_FIELD[type]]: result.path } : m
      ));

      setPreviewPDF({
        url: result.url,
        title: `${pdfTypes[type]} — ${mailing.order_number}`,
        type
      });
    } catch (err) {
      console.error('PDF generation error:', err);
      // The API returns messages written for an operator — "No PVDS template
      // has been uploaded", "Pack this mailing into boxes first" — so show them
      // as-is rather than burying them behind a generic failure.
      setError(`${pdfTypes[type] || type}: ${err.message}`);
      setTimeout(() => setError(""), 6000);
    } finally {
      setGeneratingPDF(null);
    }
  };

  const handleClosePDFViewer = () => {
    if (previewPDF && previewPDF.url.startsWith('blob:')) {
      URL.revokeObjectURL(previewPDF.url);
    }
    setPreviewPDF(null);
  };

  const handleHandDelivery = async (mailing) => {
    try {
      // One call: the server writes a row per container in a single insert and
      // sets shipment_type to hand_delivery, which the old client-side loop
      // never did — leaving hand deliveries indistinguishable from unset rows.
      await api.shipping.handDelivery(mailing.id);
      await handleShippingComplete(mailing.id, mailing.mailing_name, mailing.order_number, true);
    } catch (err) {
      console.error("Failed to mark as delivered:", err);
      setError(err.message || "Failed to mark as delivered");
      setTimeout(() => setError(""), 5000);
    }
  };

  const handleCardClick = async (mailing, e) => {
    if (e.target.closest('button') || e.target.closest('input[type="checkbox"]')) {
      return;
    }
    
    await logActivity(mailing.id, mailing.mailing_name, mailing.order_number, 'EDIT_MAILING', 'Opened mailing for editing', currentUser);

    // The row on this card is a summary. Routes live in their own table, the
    // permit block is four columns rather than the object the editor holds,
    // and the documents are references - so opening a mailing from the card
    // alone lost all three, silently, because every one of those reads is an
    // optional chain that yields nothing.
    let full;
    try {
      full = await api.mailings.full(mailing.id);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Could not open that mailing.');
      return;
    }

    navigate(createPageUrl('Review'), {
      state: {
        parsedData: {
          routes: full.routes,
          permit_info: full.permit_info
        },
        mailingDate: full.mailing_date,
        mailingName: full.mailing_name,
        orderNumber: full.order_number,
        pieceWeight: full.piece_weight,
        existingMailing: full,
        isReadOnly: true
      }
    });
  };

  const allMailingsCount = useMemo(
    () => mailings.filter((m) => !hasShipments(m)).length,
    [mailings]
  );
  const shippedMailingsCount = useMemo(
    () => mailings.filter(hasShipments).length,
    [mailings]
  );

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading">Loading mailings...</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <header className="header">
        <div className="header-title">Processed Mailings</div>
        <div style={{ display: "flex", gap: "0.75rem" }}>
          {selectedMailings.length > 0 && (
            <button
              className="btn btn-outline"
              onClick={() => setDeleteConfirm("bulk")}
              style={{ color: "#dc2626" }}
            >
              <Trash2 size={20} />
              Delete Selected ({selectedMailings.length})
            </button>
          )}
          <button
            className="btn"
            onClick={() => setShowActivityLogs(true)}
            style={{
              background: "#fef3c7",
              color: "#92400e",
              border: "2px solid #fbbf24"
            }}
          >
            <ClipboardList size={20} />
            Activity Logs
          </button>
        </div>
      </header>

      {error && <div className="error">{error}</div>}

      {/* Tabs */}
      <div style={{
        display: "flex",
        gap: "0.5rem",
        marginBottom: "1.5rem",
        borderBottom: "2px solid #e2e8f0",
        paddingBottom: "0.5rem"
      }}>
        <button
          onClick={() => setActiveTab("all")}
          style={{
            padding: "0.75rem 1.5rem",
            background: activeTab === "all" ? "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)" : "white",
            color: activeTab === "all" ? "white" : "#64748b",
            border: activeTab === "all" ? "none" : "2px solid #e2e8f0",
            borderRadius: "8px",
            fontWeight: 600,
            cursor: "pointer",
            transition: "all 0.2s",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem"
          }}
        >
          <Package size={16} />
          All Mailings ({allMailingsCount})
        </button>
        <button
          onClick={() => setActiveTab("shipped")}
          style={{
            padding: "0.75rem 1.5rem",
            background: activeTab === "shipped" ? "linear-gradient(135deg, #10b981 0%, #059669 100%)" : "white",
            color: activeTab === "shipped" ? "white" : "#64748b",
            border: activeTab === "shipped" ? "none" : "2px solid #e2e8f0",
            borderRadius: "8px",
            fontWeight: 600,
            cursor: "pointer",
            transition: "all 0.2s",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem"
          }}
        >
          <Truck size={16} />
          Shipped ({shippedMailingsCount})
        </button>
      </div>

      {/* Search and Filter Section */}
      <div className="form-section">
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">
              <Search size={16} style={{ display: "inline", marginRight: "0.25rem" }} />
              Search
            </label>
            <input
              type="text"
              className="form-input"
              placeholder="Search by name or order number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Date From</label>
            <input
              type="date"
              className="form-input"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Date To</label>
            <input
              type="date"
              className="form-input"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Mailings List */}
      {filteredMailings.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: "3rem" }}>
          <Package size={64} style={{ margin: "0 auto 1rem", color: "#94a3b8" }} />
          <h3 style={{ fontSize: "1.25rem", fontWeight: 600, color: "#475569", marginBottom: "0.5rem" }}>
            {activeTab === "shipped" ? "No Shipped Mailings" : 
             searchTerm || dateFrom || dateTo ? "No Matching Mailings" : "No Mailings Yet"}
          </h3>
          <p style={{ color: "#64748b", marginBottom: "1.5rem" }}>
            {activeTab === "shipped" ? "Mailings will appear here after you create shipping labels" :
             searchTerm || dateFrom || dateTo 
              ? "Try adjusting your search filters" 
              : "Upload a PS Form 3602 to create your first mailing"}
          </p>
          {!searchTerm && !dateFrom && !dateTo && activeTab === "all" && (
            <button className="btn btn-primary" onClick={() => navigate(createPageUrl('Home'))}>
              Get Started
            </button>
          )}
        </div>
      ) : (
        <>
          <div style={{ marginBottom: "1rem", padding: "0.75rem", background: "white", borderRadius: "8px" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={selectedMailings.length === filteredMailings.length && filteredMailings.length > 0}
                onChange={handleSelectAll}
                style={{ width: "18px", height: "18px", cursor: "pointer" }}
              />
              <span style={{ fontWeight: 600, color: "#475569" }}>
                Select All ({filteredMailings.length} mailings)
              </span>
            </label>
          </div>

          <div style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", 
            gap: "1rem"
          }}>
            {filteredMailings.map((mailing) => (
              <MailingCard
                key={mailing.id}
                mailing={mailing}
                isSelected={selectedMailings.includes(mailing.id)}
                onSelect={() => handleSelectMailing(mailing.id)}
                onClick={(e) => handleCardClick(mailing, e)}
                hasShipments={hasShipments(mailing)}
                onPackBoxes={() => setPackingModalMailing(mailing)}
                onDeliver={() => handleHandDelivery(mailing)}
                onShip={() => {
                  const isOverride = mailing.shipping_override?.enabled && mailing.shipping_override?.address_line1;
                  if (isOverride) {
                    setShipMethodMailing(mailing);
                  } else {
                    setShippingModalMailing(mailing);
                  }
                }}
                onViewShipment={() => handleViewShipment(mailing)}
                onPreviewPDF={(type) => {
                  if (type === 'pvds') {
                    setPvdsModalMailing(mailing);
                  } else {
                    handlePreviewPDF(mailing, type);
                  }
                }}
                onDelete={() => setDeleteConfirm(mailing.id)}
                generatingPDF={generatingPDF?.split('-')[1]}
              />
            ))}
          </div>

          <div style={{ display: "flex", justifyContent: "center", marginTop: "2rem", paddingBottom: "2rem" }}>
            <button
              className="btn btn-outline"
              onClick={() => setMailingLimit(prev => prev + 500)}
              style={{ padding: "0.75rem 1.5rem", fontSize: "0.9rem" }}
            >
              Load More Mailings
            </button>
          </div>
        </>
      )}

      {/* Box Packing Modal */}
      {packingModalMailing && (
        <BoxPackingModal
          mailing={packingModalMailing}
          onClose={() => setPackingModalMailing(null)}
          onSave={handlePackingComplete}
        />
      )}

      {/* Ship Method Dialog (for override mailings) */}
      {shipMethodMailing && (
        <ShipMethodDialog
          mailing={shipMethodMailing}
          onClose={() => setShipMethodMailing(null)}
          onSelectShippo={() => {
            setShippingModalMailing(shipMethodMailing);
            setShipMethodMailing(null);
          }}
          onFreightComplete={async () => {
            setShipMethodMailing(null);
            /*
             * Refresh only. The freight endpoint records its own
             * FREIGHT_RECORDED entry, naming the carrier, the PRO number and
             * the container count, so going through handleShippingComplete
             * added a second entry reading "Created shipping labels" - for a
             * shipment that produces no labels at all.
             */
            await fetchMailings();
          }}
        />
      )}

      {/* Shipping Labels Modal */}
      {shippingModalMailing && (
        <ShippingLabelsModal
          mailing={shippingModalMailing}
          onClose={() => setShippingModalMailing(null)}
          onComplete={handleShippingComplete}
        />
      )}

      {/* PVDS Modal */}
      {pvdsModalMailing && (
        <PostOfficePVDSModal
          mailing={pvdsModalMailing}
          onClose={() => setPvdsModalMailing(null)}
          onOpenPDF={(pdfData) => {
            setPvdsModalMailing(null);
            setPreviewPDF(pdfData);
          }}
        />
      )}

      {/* PDF Viewer Modal */}
      {previewPDF && (
        <PDFViewerModal
          pdfUrl={previewPDF.url}
          title={previewPDF.title}
          onClose={handleClosePDFViewer}
        />
      )}

      {/* Activity Logs Modal */}
      {showActivityLogs && (
        <ActivityLogsModal
          onClose={() => setShowActivityLogs(false)}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000
        }}>
          <div className="card" style={{ maxWidth: "400px", width: "90%" }}>
            <h3 style={{ fontSize: "1.25rem", fontWeight: 600, marginBottom: "1rem" }}>
              Confirm Delete
            </h3>
            <p style={{ color: "#64748b", marginBottom: "1.5rem" }}>
              {deleteConfirm === "bulk" 
                ? `Are you sure you want to delete ${selectedMailings.length} selected mailings? This action cannot be undone.`
                : "Are you sure you want to delete this mailing? This action cannot be undone."}
            </p>
            <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}>
              <button
                className="btn btn-outline"
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                style={{ background: "#dc2626" }}
                onClick={() => deleteConfirm === "bulk" ? handleDeleteSelected() : handleDelete(deleteConfirm)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/*
        * The order has shipped, so its labels have to be refunded first.
        *
        * Offered rather than refused: this is the ordinary way an order gets
        * cancelled, and sending someone to another screen to remove boxes one
        * at a time - then back here - is a dead end dressed up as a safeguard.
        * The money still comes back first; the server refunds each label before
        * removing it, and stops if a refund fails.
        */}
      {shipmentsInTheWay && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0, 0, 0, 0.5)", display: "flex",
          alignItems: "center", justifyContent: "center", zIndex: 1000
        }}>
          <div className="card" style={{ maxWidth: "520px", width: "90%" }}>
            <h3 style={{ fontSize: "1.25rem", fontWeight: 600, marginBottom: "0.75rem" }}>
              This order has {shipmentsInTheWay.count} shipment
              {shipmentsInTheWay.count === 1 ? "" : "s"}
            </h3>
            <p style={{ color: "#64748b", marginBottom: "0.75rem" }}>
              Order {shipmentsInTheWay.mailing?.order_number} cannot be deleted while its
              labels exist. Refund and remove {shipmentsInTheWay.count === 1 ? "it" : "them"} first,
              then delete the order?
            </p>

            {shipmentsInTheWay.names.length > 0 && (
              <ul style={{
                margin: "0 0 1rem", padding: "0.75rem 1rem", listStyle: "none",
                background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "8px",
                maxHeight: "9rem", overflowY: "auto", fontSize: "0.875rem", color: "#334155"
              }}>
                {shipmentsInTheWay.names.map((name) => (
                  <li key={name} style={{ padding: "0.125rem 0" }}>{name}</li>
                ))}
              </ul>
            )}

            <p style={{ color: "#64748b", fontSize: "0.875rem", marginBottom: "1.5rem" }}>
              Each label is refunded through Shippo before its shipment is removed. If a
              refund fails, everything stops there and the order is left alone.
            </p>

            <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}>
              <button
                className="btn btn-outline"
                disabled={deletingMailing === shipmentsInTheWay.mailingId}
                onClick={() => setShipmentsInTheWay(null)}
              >
                Keep the order
              </button>
              <button
                className="btn btn-primary"
                style={{ background: "#dc2626" }}
                disabled={deletingMailing === shipmentsInTheWay.mailingId}
                onClick={() => handleDelete(shipmentsInTheWay.mailingId, { withShipments: true })}
              >
                {deletingMailing === shipmentsInTheWay.mailingId
                  ? "Refunding..."
                  : `Refund ${shipmentsInTheWay.count === 1 ? "it" : "them"} and delete the order`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}